import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/utils';
import { appLayerProps } from './app-layer';

/**
 * A menu opened at an arbitrary screen point (a right-click on a block),
 * not at a real trigger element a user clicked. Built on the same Radix
 * primitive `DropdownMenu` uses — real focus trap, keyboard nav (arrows,
 * type-ahead, Home/End), Escape + outside-click dismissal, and
 * viewport-clamped placement all come from Radix, none of it hand-rolled
 * here (DESIGN §4 / no hand-rolled menu, Modules R2 Phase 5).
 *
 * There is no DOM element a right-click naturally anchors to, so the
 * caller renders an invisible, `position: fixed` element as
 * `ContextMenuTrigger`'s child (asChild) at the click point, and drives
 * `open` itself. See `spec-studio/SpecBlockMenu.tsx` for the pattern.
 */
export const ContextMenu = DropdownMenuPrimitive.Root;
export const ContextMenuTrigger = DropdownMenuPrimitive.Trigger;
export const ContextMenuGroup = DropdownMenuPrimitive.Group;
export const ContextMenuPortal = DropdownMenuPrimitive.Portal;
export const ContextMenuSub = DropdownMenuPrimitive.Sub;
export const ContextMenuSubTrigger = DropdownMenuPrimitive.SubTrigger;
export const ContextMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      {...appLayerProps}
      className={cn('z-popover outline-none', className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
ContextMenuContent.displayName = 'ContextMenuContent';

export const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item ref={ref} className={cn('outline-none', className)} {...props} />
));
ContextMenuItem.displayName = 'ContextMenuItem';

export const ContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn('outline-none', className)}
    {...props}
  />
));
ContextMenuCheckboxItem.displayName = 'ContextMenuCheckboxItem';

export const ContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem ref={ref} className={cn('outline-none', className)} {...props} />
));
ContextMenuRadioItem.displayName = 'ContextMenuRadioItem';

export const ContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label ref={ref} className={cn(className)} {...props} />
));
ContextMenuLabel.displayName = 'ContextMenuLabel';

export const ContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator ref={ref} className={cn(className)} {...props} />
));
ContextMenuSeparator.displayName = 'ContextMenuSeparator';

/** Flyout content for a `ContextMenuSub` (e.g. "Move to ▸"). Tasks Kit is
 *  the first caller — mirrors `ContextMenuContent` exactly. */
export const ContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      {...appLayerProps}
      className={cn('z-popover outline-none', className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
ContextMenuSubContent.displayName = 'ContextMenuSubContent';
